import apiClient from "./client";

// Note: apiClient already unwraps the axios response (returns response.data),
// so `response` below is the response body.
export const leaveApi = {
  /* ---------- Leave types ---------- */

  // GET /leave-types (used by dropdowns)
  getLeaveTypes: async () => {
    const response = await apiClient.get("/leave-types");
    return response || [];
  },

  // POST /leave-types
  createLeaveType: async (data) => {
    const response = await apiClient.post("/leave-types", data);
    return response.data;
  },

  // PATCH /leave-types/:id
  updateLeaveType: async (id, data) => {
    const response = await apiClient.patch(`/leave-types/${id}`, data);
    return response.data;
  },

  // DELETE /leave-types/:id
  deleteLeaveType: async (id) => {
    const response = await apiClient.delete(`/leave-types/${id}`);
    return response.data;
  },

  /* ---------- Balances ---------- */

  /**
   * GET /leave/employees/balances (Admin/HR) → { year, meta: { total, page, limit, totalPages }, data }
   * Each employee has `leaveBalances[]` (numbers); a missing type = not allocated that year.
   * params: year, search (name or employee no.), branchId, companyId, leaveTypeId,
   *         includeSeparated, page, limit (max 100)
   */
  getAllEmployeeBalance: (params = {}) =>
    apiClient.get("/leave/employees/balances", {
      params: Object.fromEntries(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
        ),
      ),
    }),

  /**
   * GET /leave/my-balances/:employeeId → the employee's balances.
   * Default: current cycles (containing today). params: asOf (YYYY-MM-DD), year, leaveTypeId.
   * Each balance has periodStart / periodEnd (the anniversary cycle).
   */
  getLeaveBalanceEmployee: async (employeeId, params = {}) => {
    const response = await apiClient.get(`/leave/my-balances/${employeeId}`, {
      params: Object.fromEntries(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      ),
    });
    return response;
  },

  /**
   * POST /leave/update-allocation → the balance + { previousAllocated, changed }
   * Creates the cycle if missing (policy + eligibility checked). Only these fields are allowed (others → 400).
   */
  updateAllocation: ({
    employeeId,
    leaveTypeId,
    periodStart,
    year,
    allocatedDays,
    reason,
  }) =>
    apiClient.post("/leave/update-allocation", {
      employeeId,
      leaveTypeId,
      // The cycle: periodStart (preferred) or the old `year`; neither = today's cycle
      ...(periodStart ? { periodStart } : year ? { year } : {}),
      allocatedDays,
      ...(reason && reason.trim() && { reason: reason.trim() }),
    }),

  // GET /leave/allocation-history/:employeeId?year= → newest first (max 200); year = year of periodStart; omit for all
  // action: CREATE | UPDATE (HR), REPLENISH (job created the cycle), CLOSE (cycle ended; lapsedDays)
  getAllocationHistory: (employeeId, year) =>
    apiClient.get(`/leave/allocation-history/${employeeId}`, {
      params: year ? { year } : {},
    }),

  /* ---------- Leave requests ---------- */

  // GET /leave/company/:companyId/hierarchy/count → number
  getCompanyRequestCount: ({ companyId, branchId, leaveTypeId, status } = {}) =>
    apiClient
      .get(`/leave/company/${companyId}/hierarchy/count`, {
        params: {
          ...(branchId && branchId !== "All" && { branchId }),
          ...(leaveTypeId && leaveTypeId !== "All" && { leaveTypeId }),
          ...(status && status !== "All" && { status }),
        },
      })
      .then((res) =>
        typeof res === "object" && res !== null ? res.count : res,
      ),

  // GET /leave/:employeeId
  getEmployeeLeaveRequests: async (employeeId, leaveTypeId = "") => {
    const params = leaveTypeId ? { leaveTypeId } : {};
    const response = await apiClient.get(`/leave/${employeeId}`, { params });
    return response;
  },

  // POST /leave/request { employeeId, leaveTypeId, startDate, endDate, totalDays, reason }
  // HR/approvers can file for an employee in their scope.
  createLeaveRequest: (data) => apiClient.post("/leave/request", data),

  /* ---------- Review (approver / HR) ---------- */

  /**
   * GET /leave/requests → { data, total, page, limit }
   * Only requests the user may act on; their own are left out.
   * filters: status (pending|approved|rejected|cancelled), leaveTypeId, branchId,
   *          employeeId, search, from, to (YYYY-MM-DD), myTurn (true = my level now), page, limit (max 100)
   * Rows include levels { current, total } and canReview.
   */
  getRequests: (filters = {}) =>
    apiClient.get("/leave/requests", {
      params: Object.fromEntries(
        Object.entries(filters).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      ),
    }),

  // GET /leave/requests/:id → { request, balance, canReview }
  getRequest: (id) => apiClient.get(`/leave/requests/${id}`),

  // GET /leave/:id/can-approve → { canApprove } (uses the token)
  canApprove: (id) =>
    apiClient
      .get(`/leave/${id}/can-approve`)
      .then((res) => (typeof res === "boolean" ? res : !!res?.canApprove)),

  // PATCH /leave/:requestId/approval  { remarks?, allLevels? (HR: approve every remaining level) }
  // Multi-level: stays pending until the last level
  approveLeave: (requestId, remarks, allLevels) =>
    apiClient.patch(`/leave/${requestId}/approval`, {
      ...(remarks ? { remarks } : {}),
      ...(allLevels ? { allLevels: true } : {}),
    }),

  // PATCH /leave/:requestId/reject  { remarks } (required)
  rejectLeave: (requestId, remarks) =>
    apiClient.patch(`/leave/${requestId}/reject`, { remarks }),

  // PATCH /leave/:requestId/cancel (pending or approved; days go back to the balance)
  cancelLeave: (requestId) => apiClient.patch(`/leave/${requestId}/cancel`, {}),

  /* ---------- Anniversary replenish job (Admin/HR) ---------- */

  // GET /leave/replenish-runs?limit= → runs, newest first
  getReplenishRuns: (limit = 10) =>
    apiClient.get("/leave/replenish-runs", { params: { limit } }),

  // POST /leave/replenish/run?date=&dryRun= with an empty JSON body → run summary (+ runId; null for dry runs)
  runReplenish: ({ date, dryRun }) =>
    apiClient.post(
      "/leave/replenish/run",
      {},
      { params: { ...(date && { date }), dryRun: !!dryRun } },
    ),
};
